import { useParams } from 'react-router-dom';
import React, { useEffect, useState } from 'react';
import EmailFormModal from '../Components/EmailModal.js';
import '../CSS/Orgs.css';

const OrgDetailsPage = () => {
  const { id } = useParams();

  const [orgs, setOrgs] = useState([]);
  const [reports, setReports] = useState([]);
  const [orgPhotos, setOrgPhotos] = useState([]);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [logoLoaded, setLogoLoaded] = useState(false);
  const [logoFailed, setLogoFailed] = useState(false);

  const openReport = (linkUrl, reportType = 'GroupsReports') => {
    const userEmail = localStorage.getItem('user-email');

    if (!userEmail) {
      alert('You must be logged in to view this report.');
      return;
    }

    const filename = linkUrl.split('/').pop();
    const reportUrl = `/api/reports/${encodeURIComponent(filename)}?email=${encodeURIComponent(userEmail)}`;

    fetch('/api/log-report', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        reportName: filename,
        reportType,
        userEmail,
      }),
    }).catch(() => {});

    window.open(reportUrl, '_blank');
  };

  useEffect(() => {
    fetch('/api/hello', {
      credentials: 'include',
      cache: 'no-store',
    })
      .then((response) => response.json())
      .then((data) => {
        setOrgs(data.orgs || []);
        setReports(data.reports || []);
      })
      .catch((error) => {
        console.error('Error fetching orgs data:', error);
      });
  }, []);

  const selectedOrg = orgs.find((org) => String(org.fields.id) === String(id));

  useEffect(() => {
    if (!selectedOrg?.fields?.Title) return;

    setOrgPhotos([]);

    fetch(`/api/org-photos/${encodeURIComponent(selectedOrg.fields.Title)}`, {
      credentials: 'include',
      cache: 'no-store',
    })
      .then((res) => res.json())
      .then((data) => setOrgPhotos(data.photos || []))
      .catch((err) => console.error('Error loading org photos:', err));
  }, [selectedOrg?.fields?.Title]);

  useEffect(() => {
    setLogoLoaded(false);
    setLogoFailed(false);
  }, [id]);

  if (!selectedOrg) {
    return <div className="orgs">Organization not found</div>;
  }

  const profileLink = selectedOrg.fields.ProfileLink;
  const orgTitle = selectedOrg.fields.Title;
  const akaDash = selectedOrg.fields.AKA ? `- ${selectedOrg.fields.AKA}` : '';

  const hasLogoField = Boolean(selectedOrg.fields.Logo);

  const profileProxyLink = profileLink
    ? `/api/highlights/Profiles/${encodeURIComponent(profileLink.split('/').pop())}`
    : null;

  const orgLogoUrl = `/api/org-logo/${encodeURIComponent(
    selectedOrg.id || selectedOrg.fields.id
  )}`;

  const orgReports = reports.filter((report) => {
    const oldData = report.fields.field_10 ? report.fields.field_10.trim() : '';

    const expectedOrg1 = orgTitle ? orgTitle.trim() : '';
    const expectedOrg2 = selectedOrg.fields.OldName
      ? selectedOrg.fields.OldName.trim()
      : '';

    const legacyMatch =
      oldData !== '' && (oldData === expectedOrg1 || oldData === expectedOrg2);

    let newDataMatch = false;

    if (Array.isArray(report.fields.GroupResponsibleLU)) {
      newDataMatch = report.fields.GroupResponsibleLU.some((item) => {
        const lookupVal = item.LookupValue ? item.LookupValue.trim() : '';
        return lookupVal === expectedOrg1 || lookupVal === expectedOrg2;
      });
    }

    return legacyMatch || newDataMatch;
  });

  return (
    <div className="orgs">
      <div className="org-header-card">
        {hasLogoField && !logoFailed && (
          <div
            className="org-logo-wrap"
            style={{ display: logoLoaded ? 'flex' : 'none' }}
          >
            <img
              src={orgLogoUrl}
              alt={`${selectedOrg.fields.Title} logo`}
              className="org-logo"
              onLoad={() => setLogoLoaded(true)}
              onError={() => {
                setLogoLoaded(false);
                setLogoFailed(true);
              }}
            />
          </div>
        )}

        <div className="org-header-text">
          <h1>
            {selectedOrg.fields.Title} {akaDash}
          </h1>

          {selectedOrg.fields.OldName && (
            <h2>Prior Name : {selectedOrg.fields.OldName}</h2>
          )}
        </div>
      </div>

      {profileLink ? (
        <a
          href={profileProxyLink || profileLink}
          className="profiles"
          target="_blank"
          rel="noopener noreferrer"
        >
          Link to Organization Profile
        </a>
      ) : (
        <p>
          No profile exists.{' '}
          <button className="contact-button" onClick={() => setIsModalOpen(true)}>
            Click here to contact us.
          </button>
        </p>
      )}

      <div className={orgPhotos.length > 0 ? 'org-content-layout' : 'org-content-layout no-photos'}>
        <div className="org-reports-section">
          <h3>Reports Involving {selectedOrg.fields.Title}</h3>

          <div className="home-reports-grid">
            <div className="home-reports-header">
              <div className="home-header-item">Title</div>
              <div className="home-header-item">Date</div>
              <div className="home-header-item">Country</div>
              <div className="home-header-item">Sector</div>
            </div>

            {orgReports.map((report) => (
              <div className="home-report-row" key={report.fields.Id}>
                {report.fields.Links1 ? (
                  <a
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      openReport(report.fields.Links1);
                    }}
                    className="home-report-item home-report-title"
                    data-label="Title"
                  >
                    {report.fields.Title}
                  </a>
                ) : (
                  <div className="home-report-item">{report.fields.Title}</div>
                )}

                <div className="home-report-item">{report.fields.field_1_formatted}</div>
                <div className="home-report-item">{report.fields.field_3}</div>
                <div className="home-report-item">{report.fields.field_6}</div>
              </div>
            ))}
          </div>
        </div>

        {orgPhotos.length > 0 && (
          <div className="org-photos-section">
            <h3>Photo Library</h3>

            <div className="org-photo-gallery">
              {orgPhotos.map((photo) => (
                <img
                  key={photo.id}
                  src={photo.proxyUrl}
                  alt={photo.name}
                  className="org-photo"
                  loading="lazy"
                  onClick={() => setSelectedPhoto(photo)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {selectedPhoto && (
        <div className="photo-modal-overlay" onClick={() => setSelectedPhoto(null)}>
          <div className="photo-modal-content" onClick={(e) => e.stopPropagation()}>
            <button
              className="photo-modal-close"
              onClick={() => setSelectedPhoto(null)}
            >
              ×
            </button>

            <img
              src={selectedPhoto.proxyUrl}
              alt={selectedPhoto.name}
              className="photo-modal-image"
            />

            <p className="photo-modal-caption">{selectedPhoto.name}</p>
          </div>
        </div>
      )}

      <EmailFormModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        orgName={selectedOrg.fields.Title}
      />
    </div>
  );
};

export default OrgDetailsPage;